/**
 * Pack-opening modal (M1c) — the spend loop. Buy a pack → the SERVER rolls (src/app/economy.ts
 * openPack) → reveal the pulled cards one at a time with rarity flair, NEW badges, and dupe→shard
 * payouts. Pure DOM/UI; the host wires the buy action + coin balance. Card faces reuse cardFace.ts.
 */
import { getClub } from '../sim/clubs.ts';
import { getDefenseCard } from '../sim/cards.ts';
import { fillCardFace } from './cardFace.ts';

export interface PackCardResult {
  readonly id: string;
  readonly rarity: string;
  readonly isNew: boolean;
}
export interface PackOpenResult {
  readonly cards: readonly PackCardResult[];
  readonly coins: number;
  readonly gems: number;
}

export interface PackShopDeps {
  /** Pack cost in coins. */
  readonly cost: number;
  /** Current coin balance (read each time the shop opens). */
  coins(): number;
  /** Open a pack on the server. Resolves to the reveal, or `{error}`. */
  open(): Promise<PackOpenResult | { error: string }>;
}

export interface PackShop {
  open(): void;
  readonly el: HTMLElement;
}

const STYLE_ID = 'fc-pack-style';
const CSS = `
.fc-pack{position:absolute;inset:0;z-index:62;display:none;align-items:center;justify-content:center;
  background:rgba(6,12,18,.84);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);
  padding:calc(16px + env(safe-area-inset-top)) 14px calc(16px + env(safe-area-inset-bottom));
  font-family:var(--font-pixel,monospace);}
.fc-pack.is-open{display:flex;}
.fc-pack__panel{width:min(470px,95vw);max-height:94%;overflow-y:auto;background:var(--dusk-2,#1a3145);
  border:1px solid #2c4255;border-radius:16px;box-shadow:0 16px 48px rgba(0,0,0,.55);color:#f4efe2;padding:18px;text-align:center;}
.fc-pack__title{font-size:13px;color:var(--gold,#f2c14e);margin-bottom:6px;letter-spacing:.02em;}
.fc-pack__sub{font-size:9px;opacity:.72;line-height:1.8;margin-bottom:14px;}
.fc-pack__btn{appearance:none;border:0;border-radius:999px;background:var(--gold,#f2c14e);color:#1a1205;
  font-family:inherit;font-size:11px;padding:11px 22px;cursor:pointer;margin:5px;}
.fc-pack__btn--ghost{background:transparent;border:1px solid #2c4255;color:#cfd9e2;}
.fc-pack__btn:disabled{opacity:.4;cursor:not-allowed;}
.fc-pack__row{display:flex;flex-wrap:wrap;gap:10px 9px;justify-content:center;margin:6px 0 16px;min-height:108px;}
.fc-pack-card{position:relative;width:78px;height:104px;}
.fc-pack-card__back{position:absolute;inset:0;border-radius:9px;background:linear-gradient(160deg,#22384a,#15222d);
  border:1px solid #2c4255;display:flex;align-items:center;justify-content:center;color:var(--gold,#f2c14e);font-size:26px;}
.fc-pack-card.is-revealed .fc-pack-card__back{display:none;}
.fc-pack-card .fc-card{width:78px;height:104px;animation:fc-pack-pop .26s cubic-bezier(.2,1.3,.5,1);}
.fc-pack-card.rar-rare.is-revealed .fc-card{box-shadow:0 0 9px var(--rarity-rare,#5aa9ff);}
.fc-pack-card.rar-epic.is-revealed .fc-card{box-shadow:0 0 15px var(--rarity-epic,#b06cff);}
.fc-pack-card.rar-legend.is-revealed .fc-card{box-shadow:0 0 22px var(--rarity-legend,#f2c14e);animation:fc-pack-glow 1.1s ease-in-out infinite;}
.fc-pack-card__badge{position:absolute;bottom:-7px;left:50%;transform:translateX(-50%);z-index:2;font-size:7px;
  padding:2px 6px;border-radius:999px;white-space:nowrap;display:none;}
.fc-pack-card.is-revealed .fc-pack-card__badge{display:block;}
.fc-pack-card__badge--new{background:var(--gold,#f2c14e);color:#1a1205;}
.fc-pack-card__badge--dupe{background:#2c4255;color:#cfd9e2;}
.fc-pack__hint{font-size:9px;opacity:.75;margin:2px 0 10px;min-height:13px;}
@keyframes fc-pack-pop{0%{transform:scale(.55);opacity:0;}100%{transform:scale(1);opacity:1;}}
@keyframes fc-pack-glow{0%,100%{box-shadow:0 0 16px var(--rarity-legend,#f2c14e);}50%{box-shadow:0 0 28px var(--rarity-legend,#f2c14e);}}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export function createPackShop(root: HTMLElement, deps: PackShopDeps): PackShop {
  ensureStyle();

  const el = document.createElement('div');
  el.className = 'fc-pack';
  const panel = document.createElement('div');
  panel.className = 'fc-pack__panel';
  const title = document.createElement('div');
  title.className = 'fc-pack__title';
  const sub = document.createElement('div');
  sub.className = 'fc-pack__sub';
  const row = document.createElement('div');
  row.className = 'fc-pack__row';
  const hint = document.createElement('div');
  hint.className = 'fc-pack__hint';
  const buttons = document.createElement('div');
  panel.append(title, sub, row, hint, buttons);
  el.appendChild(panel);
  root.appendChild(el);

  let busy = false;

  const close = (): void => {
    el.classList.remove('is-open');
  };

  /** The buy screen: pack art-row placeholder, cost, balance, Open / Close. */
  function showBuy(message?: string): void {
    title.textContent = 'Standard Pack';
    const coins = deps.coins();
    const afford = coins >= deps.cost;
    sub.textContent = message ?? `5 cards · chance at rare, epic & legendary.\nCost ${deps.cost} 🪙   ·   You have ${coins} 🪙`;
    row.replaceChildren();
    const art = document.createElement('div');
    art.className = 'fc-pack-card';
    const back = document.createElement('div');
    back.className = 'fc-pack-card__back';
    back.textContent = '🎴';
    art.appendChild(back);
    row.appendChild(art);
    hint.textContent = '';
    buttons.replaceChildren();
    const openBtn = document.createElement('button');
    openBtn.type = 'button';
    openBtn.className = 'fc-pack__btn';
    openBtn.textContent = afford ? `Open · ${deps.cost} 🪙` : 'Not enough 🪙';
    openBtn.disabled = !afford || busy;
    openBtn.addEventListener('click', () => void doOpen());
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'fc-pack__btn fc-pack__btn--ghost';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', close);
    buttons.append(openBtn, closeBtn);
  }

  async function doOpen(): Promise<void> {
    if (busy) return;
    busy = true;
    hint.textContent = 'Opening…';
    buttons.replaceChildren();
    const result = await deps.open();
    busy = false;
    if ('error' in result) {
      showBuy(result.error === 'insufficient funds' ? 'Not enough coins for this pack.' : 'Could not open the pack — try again.');
      return;
    }
    showReveal(result);
  }

  /** The reveal: face-down cards flipped one at a time on tap. */
  function showReveal(result: PackOpenResult): void {
    title.textContent = 'Your Pack';
    sub.textContent = '';
    row.replaceChildren();
    const slots = result.cards.map((rc) => {
      const slot = document.createElement('div');
      slot.className = `fc-pack-card rar-${rc.rarity}`;
      const back = document.createElement('div');
      back.className = 'fc-pack-card__back';
      back.textContent = '?';
      const card = getClub(rc.id) ?? getDefenseCard(rc.id);
      const face = document.createElement('div');
      if (card) {
        face.className = `fc-card fc-card--${'power' in card ? 'off' : 'def'}`;
        face.style.display = 'none';
        fillCardFace(face, card);
      }
      const badge = document.createElement('span');
      badge.className = `fc-pack-card__badge fc-pack-card__badge--${rc.isNew ? 'new' : 'dupe'}`;
      badge.textContent = rc.isNew ? 'NEW' : '+10 ⚙';
      slot.append(back, face, badge);
      row.appendChild(slot);
      return { slot, face };
    });

    let revealed = 0;
    const revealNext = (): void => {
      if (revealed >= slots.length) return;
      const s = slots[revealed];
      if (s) {
        s.slot.classList.add('is-revealed');
        s.face.style.display = '';
      }
      revealed++;
      if (revealed >= slots.length) {
        el.removeEventListener('click', onReveal);
        finish(result);
      }
    };
    const onReveal = (e: Event): void => {
      // Ignore taps on the footer buttons (there are none yet during reveal) / outside the panel.
      if (e.target instanceof Node && !panel.contains(e.target)) return;
      revealNext();
    };
    hint.textContent = 'Tap to reveal';
    buttons.replaceChildren();
    el.addEventListener('click', onReveal);
  }

  function finish(result: PackOpenResult): void {
    const news = result.cards.filter((c) => c.isNew).length;
    hint.textContent = news > 0 ? `${news} new card${news > 1 ? 's' : ''}!` : 'Duplicates earned shards.';
    const coins = result.coins;
    buttons.replaceChildren();
    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'fc-pack__btn';
    again.textContent = `Open another · ${deps.cost} 🪙`;
    again.disabled = coins < deps.cost;
    again.addEventListener('click', () => showBuy());
    const done = document.createElement('button');
    done.type = 'button';
    done.className = 'fc-pack__btn fc-pack__btn--ghost';
    done.textContent = 'Done';
    done.addEventListener('click', close);
    buttons.append(again, done);
  }

  return {
    el,
    open(): void {
      busy = false;
      showBuy();
      el.classList.add('is-open');
    },
  };
}
